import { useState } from 'react';
import {
  Button,
  CenteredModal,
  DocumentsDropzone,
  InformationBox,
  Select,
  useBoolModalState,
  useUnlockFeature,
} from 'components';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { SessionFormSection } from './SessionFormSection';
import { useCapabilities } from 'queries';
import { Feature, Session } from '@app/api';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

const options = [
  { label: 'locales.fr', value: 'fr' },
  { label: 'locales.en', value: 'en' },
];
const sampleLinks = {
  en: 'https://minio-tdz.bilendi.net/nda-download/NDA-en-GB.docx',
  fr: 'https://minio-tdz.bilendi.net/nda-download/Accord%20de%20confidentialite%CC%81%20NDA%20FR.docx',
};

const useStyles = createUseStyles((theme: Theme) => ({
  informationBox: {
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
  },
  modalText: {
    ...theme.fonts.body,
    marginTop: theme.marginBase,
  },
  upsellButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase,
  },
  downloadSample: {
    marginTop: theme.marginBase * 2,
    paddingBottom: theme.marginBase * 2,
  },
  sampleIcon: {
    marginRight: theme.marginBase,
  },
}));

interface Props {
  session: Session;
}

export const NDADropzone = ({ session }: Props) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === 'fr';
  const [sampleLink, setSampleLink] = useState(
    isFr ? sampleLinks['fr'] : sampleLinks['en'],
  );

  const capabilities = useCapabilities(session.planId, session.organizationId);
  const unlockFeature = useUnlockFeature(Feature.NDA);
  const [sampleOpened, openSample, closeSample] = useBoolModalState();

  return (
    <SessionFormSection title={t('editSession.settings.nda.title')}>
      <InformationBox
        text={t('editSession.settings.nda.description')}
        className={classes.informationBox}
        variant="plan-upgrade"
      >
        <div className={classes.upsellButton}>
          {capabilities?.nda && (
            <Button
              text={t('editSession.settings.nda.example_title')}
              onClick={openSample}
              variant="muted"
              color="accent"
              small
              children={
                <i
                  className={classnames('ri-download-line', classes.sampleIcon)}
                ></i>
              }
            />
          )}
          <Button
            text={t('editSession.settings.nda.upsell.more')}
            href={t('editSession.settings.nda.upsell.moreLink')}
            target="_blank"
            rel="noopener noreferrer"
            variant="muted"
            color="accent"
            small
          />
          {!capabilities?.nda && (
            <Button
              onClick={unlockFeature}
              text={t('editSession.settings.nda.upsell.unlock', {
                amount: 2500,
              })}
              variant="muted"
              color="accent"
              small
            />
          )}
        </div>
      </InformationBox>
      <DocumentsDropzone sessionId={session.id} disabled={!capabilities?.nda} />
      <CenteredModal
        title={t('editSession.settings.nda.example_title')}
        isOpen={sampleOpened}
        onRequestClose={closeSample}
      >
        <div className={classes.downloadSample}>
          <Select
            label={t('editSession.settings.nda.example_locale')}
            options={options}
            onChange={(option: any) => {
              if (option && typeof option === 'object' && 'value' in option) {
                const language = option.value as string;
                setSampleLink(
                  sampleLinks[language as keyof typeof sampleLinks],
                );
              }
            }}
          />
          <Button
            text={t('editSession.settings.nda.download')}
            href={sampleLink}
            target="_blank"
          />
        </div>
      </CenteredModal>
    </SessionFormSection>
  );
};
